import React, { useCallback, useEffect, useState } from 'react';
import './SupportAgentTab.css';
import './ArchiveOldTickets.css';
import { statusLabel } from './ticketStatus';

function ageDays(ms) {
  return ms ? Math.floor((Date.now() - ms) / 86400e3) : '?';
}

// Bulk-archive open/pending tickets with no activity for N days. No customer emails.
function ArchiveOldTickets({ apiBase, getToken, onClose, onArchived }) {
  const [days, setDays] = useState(5);
  const [tickets, setTickets] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const loadPreview = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/api/v1/support/tickets-inactive?days=${d}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.message || 'Failed to load');
      setTickets(data.tickets);
      setSelected(new Set(data.tickets.map((t) => t.ticketId)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, getToken]);

  useEffect(() => { loadPreview(5); }, [loadPreview]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const archive = async () => {
    if (!selected.size || !window.confirm(`Archive ${selected.size} tickets? No email is sent to customers. They can be found later under Archived.`)) return;
    setArchiving(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/api/v1/support/tickets-archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticketIds: [...selected] }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.message || 'Archive failed');
      setDone(data.archived);
      onArchived();
    } catch (err) {
      setError(err.message);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="archive-overlay" onClick={onClose}>
      <div className="archive-modal" onClick={(e) => e.stopPropagation()}>
        <div className="archive-head">
          <h3>Archive old tickets</h3>
          <button className="support-btn small" onClick={onClose}>Close</button>
        </div>

        {done != null ? (
          <p>Archived {done} tickets. No customers were emailed.</p>
        ) : (
          <>
            <div className="archive-controls">
              <label>
                No activity for
                <input
                  type="number"
                  min="1"
                  max="365"
                  className="support-input archive-days"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                />
                days
              </label>
              <button className="support-btn small" onClick={() => loadPreview(Number(days) || 5)} disabled={loading}>
                {loading ? 'Loading…' : 'Preview'}
              </button>
            </div>
            <p className="agent-tab-muted">Open and pending tickets only. Customers are not emailed. Any waiting AI draft is cancelled.</p>

            {error && <div className="agent-tab-err">{error}</div>}

            {tickets && (
              <>
                <div className="archive-summary">
                  {tickets.length} tickets match · {selected.size} selected
                  {tickets.length > 0 && (
                    <button className="agent-tab-link" onClick={() => setSelected(selected.size === tickets.length ? new Set() : new Set(tickets.map((t) => t.ticketId)))}>
                      {selected.size === tickets.length ? 'Select none' : 'Select all'}
                    </button>
                  )}
                </div>
                <ul className="agent-tab-list archive-list">
                  {tickets.map((t) => (
                    <li key={t.ticketId} className="backlog-row">
                      <input type="checkbox" checked={selected.has(t.ticketId)} onChange={() => toggle(t.ticketId)} />
                      <div className="backlog-row-body">
                        <div>
                          <strong>{t.ticketNumber}</strong> · {t.customerName}
                          <span className="agent-tab-muted"> · {ageDays(t.lastActivityMs)}d no activity · {statusLabel(t.status)}</span>
                          {t.hasPendingDraft && <span className="agent-draft-chip">has AI draft</span>}
                        </div>
                        <div className="agent-tab-muted">{t.subject}</div>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="backlog-actions">
                  <button className="support-btn primary small" onClick={archive} disabled={archiving || !selected.size}>
                    {archiving ? 'Archiving…' : `Archive selected (${selected.size}) — no message sent`}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ArchiveOldTickets;
